
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { isBrowser } from "@/utils/runtime";

const TocTocHero = () => {
  // Counter animation hooks - start with 0 to prevent hydration mismatch
  const [countImoveis, setCountImoveis] = useState(0);
  const [countHoras, setCountHoras] = useState(0);
  const [countPercent, setCountPercent] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Initialize client-side state
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Counter animation effect
  useEffect(() => {
    if (!isBrowser || !isClient || hasAnimated) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          // Animate Imóveis counter to 30
          const imoveisInterval = setInterval(() => {
            setCountImoveis(prev => {
              if (prev >= 30) {
                clearInterval(imoveisInterval);
                return 30;
              }
              return prev + 1;
            });
          }, 50);

          // Animate Horas counter to 48
          const horasInterval = setInterval(() => {
            setCountHoras(prev => {
              if (prev >= 48) {
                clearInterval(horasInterval);
                return 48;
              }
              return prev + 2;
            });
          }, 60);

          // Animate Percent counter to 98
          const percentInterval = setInterval(() => {
            setCountPercent(prev => {
              if (prev >= 98) {
                clearInterval(percentInterval);
                return 98;
              }
              return prev + 3;
            });
          }, 40);
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, isClient]);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 25% 25%, #FF5605 0%, transparent 50%), radial-gradient(circle at 75% 75%, #3332FC 0%, transparent 50%)"
        }} />
        </div>
        
        <div className="container max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="text-center lg:text-left px-0 mx-0 my-[16px]">
              <h1 className="hero-heading text-5xl md:text-6xl lg:text-7xl font-bold text-[#323031] mb-8 leading-tight animate-[fadeInUp_0.8s_ease-out_0.2s_both]">
                Fotografia Imobiliária Profissional em
                <span className="block text-[#FF5605]">Balneário Camboriú e Região</span>
              </h1>
              
              <p className="hero-subheading text-xl md:text-2xl text-[#5B5A59] mb-12 leading-relaxed max-w-2xl animate-[fadeInUp_0.8s_ease-out_0.4s_both]">
                Transformamos propriedades em experiências visuais irresistíveis que aceleram vendas e maximizam valor.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start mb-16">
                <Button
                  asChild
                  size="lg"
                  className="hero-button bg-[#FF5605] hover:bg-[#E14D04] text-white rounded-full px-10 py-6 text-lg font-semibold group shadow-xl animate-[pulse_2s_infinite_1.5s]"
                >
                  <Link to="/servicos">
                    Comece agora
                    <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
              
              {/* Stats */}
              <div ref={statsRef} className="grid grid-cols-3 gap-8 pt-8 border-t border-gray-200">
                <div className="text-center lg:text-left">
                  <div id="counter-imoveis" className="text-4xl font-bold text-[#FF5605] mb-2 counter-animation">
                    {countImoveis}k+
                  </div>
                  <div className="text-sm text-[#5B5A59] uppercase tracking-wide">Imóveis fotografados</div>
                </div>
                <div className="text-center lg:text-left">
                  <div id="counter-48h" className="text-4xl font-bold text-[#FF5605] mb-2 counter-animation">
                    {countHoras}h
                  </div>
                  <div className="text-sm text-[#5B5A59] uppercase tracking-wide">Entrega garantida</div>
                </div>
                <div className="text-center lg:text-left">
                  <div id="counter-98" className="text-4xl font-bold text-[#FF5605] mb-2 counter-animation">
                    {countPercent}%
                  </div>
                  <div className="text-sm text-[#5B5A59] uppercase tracking-wide">Clientes satisfeitos</div>
                </div>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="hero-image relative rounded-3xl overflow-hidden shadow-2xl animate-[scaleIn_0.9s_ease-out_0.5s_both]">
                <img
                  src="/lovable-uploads/632fd7b6-c855-44bc-bb3c-83bd021923d4.png"
                  alt="Fotografia profissional de imóvel em Balneário Camboriú - Exemplo de trabalho TocToc Fotografia Imobiliária"
                  loading="eager"
                  width="900"
                  height="600"
                  decoding="async"
                  fetchPriority="high"
                  className="w-full h-[600px] object-cover"
                />
                
                {/* Floating cards */}
                <div className="absolute top-8 left-8 bg-white rounded-2xl p-4 shadow-xl animate-[fadeInLeft_1s_ease-out_1s_both]">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-[#FF5605] rounded-full"></div>
                    <span className="text-sm font-medium text-[#323031]">Fotografia profissional</span>
                  </div>
                </div>
                
                <div className="absolute bottom-8 right-8 bg-white rounded-2xl p-6 shadow-xl animate-[fadeInRight_1s_ease-out_1.2s_both]">
                  <div className="text-2xl font-bold text-[#FF5605] mb-1">+35%</div>
                  <div className="text-sm text-[#5B5A59]">Mais visualizações</div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#3332FC]/10 rounded-full animate-[pulse_3s_infinite]"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#FF5605]/10 rounded-full animate-[pulse_3s_infinite_1s]"></div>
            </div>
          </div>
        </div>
      </section>
  );
};

export default TocTocHero;
